import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  getCategories,
  getStatsByCategory,
  getAllStatKeys,
  getWeaponStatKeys,
  isWeaponStat,
  type StatConfig,
} from "@/lib/config/unit-comparison-config";
import { useState, useEffect } from "react";

interface StatSelectorProps {
  selectedStats: string[];
  onStatsChange: (stats: string[]) => void;
  hasWeaponsSelected?: boolean;
}

export function StatSelector({ selectedStats, onStatsChange, hasWeaponsSelected = false }: StatSelectorProps) {
  // Get categories, filtering out Weapon if no weapons are selected
  const allCategories = getCategories();
  // Reorder categories: Weapon first, then Misc, then others
  const categoryOrder = ["Weapon", "Misc", "Movement", "Armor", "Optics", "Flying"];
  const orderedCategories = categoryOrder.filter((cat) => allCategories.includes(cat));
  const remainingCategories = allCategories.filter((cat) => !categoryOrder.includes(cat));
  const sortedCategories = [...orderedCategories, ...remainingCategories];
  
  const visibleCategories = hasWeaponsSelected 
    ? sortedCategories 
    : sortedCategories.filter((cat) => cat !== "Weapon");
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(visibleCategories));

  // Initialize with all unit stats selected (not weapon stats)
  useEffect(() => {
    if (selectedStats.length === 0) {
      const unitStats = getAllStatKeys().filter((key) => !isWeaponStat(key));
      onStatsChange(unitStats);
    }
  }, []);

  // Update visible categories when weapons selection changes
  useEffect(() => {
    if (hasWeaponsSelected) {
      setExpandedCategories((prev) => {
        const newSet = new Set(prev);
        newSet.add("Weapon");
        return newSet;
      });
    }
  }, [hasWeaponsSelected]);

  const toggleStat = (statKey: string) => {
    if (selectedStats.includes(statKey)) {
      onStatsChange(selectedStats.filter((s) => s !== statKey));
    } else {
      onStatsChange([...selectedStats, statKey]);
    }
  };

  const toggleCategory = (category: string) => {
    const categoryStats = getStatsByCategory(category).map((s) => s.statKey);
    const allCategoryStatsSelected = categoryStats.every((stat) =>
      selectedStats.includes(stat)
    );

    if (allCategoryStatsSelected) {
      // Deselect all in category
      onStatsChange(selectedStats.filter((s) => !categoryStats.includes(s)));
    } else {
      // Select all in category
      const newStats = [...selectedStats];
      categoryStats.forEach((stat) => {
        if (!newStats.includes(stat)) {
          newStats.push(stat);
        }
      });
      onStatsChange(newStats);
    }
  };

  const toggleExpandCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const selectAll = () => {
    const allStats = hasWeaponsSelected 
      ? getAllStatKeys() 
      : getAllStatKeys().filter((key) => !isWeaponStat(key));
    onStatsChange(allStats);
  };

  const deselectAll = () => {
    onStatsChange([]);
  };

  const getCategoryStats = (category: string): StatConfig[] => {
    return getStatsByCategory(category);
  };

  const isCategoryFullySelected = (category: string): boolean => {
    const categoryStats = getCategoryStats(category).map((s) => s.statKey);
    return categoryStats.length > 0 && categoryStats.every((stat) => selectedStats.includes(stat));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-2">
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={selectAll}>
            Select All
          </Button>
          <Button variant="outline" size="sm" onClick={deselectAll}>
            Deselect All
          </Button>
        </div>
      </div>
      <div className="space-y-2">
        {visibleCategories.map((category) => {
          const categoryStats = getCategoryStats(category);
          const isExpanded = expandedCategories.has(category);
          const isFullySelected = isCategoryFullySelected(category);

          return (
            <div key={category} className="border rounded-lg">
              <div className="flex items-center justify-between p-2">
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => toggleExpandCategory(category)}
                  >
                    {isExpanded ? "−" : "+"}
                  </Button>
                  <Checkbox
                    checked={isFullySelected}
                    onCheckedChange={() => toggleCategory(category)}
                    id={`category-${category}`}
                  />
                  <label
                    htmlFor={`category-${category}`}
                    className="text-sm font-medium cursor-pointer"
                  >
                    {category}
                  </label>
                </div>
              </div>
              {isExpanded && (
                <div className="pl-8 pr-2 pb-2 space-y-1">
                  {categoryStats.map((stat) => (
                    <div key={stat.statKey} className="flex items-center gap-2">
                      <Checkbox
                        checked={selectedStats.includes(stat.statKey)}
                        onCheckedChange={() => toggleStat(stat.statKey)}
                        id={`stat-${stat.statKey}`}
                      />
                      <label
                        htmlFor={`stat-${stat.statKey}`}
                        className="text-sm cursor-pointer flex-1"
                      >
                        {stat.label}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

